/**
 * 運費分攤(REQ-20260908002 圖二的「分攤面板」)—— **前後端共用同一份純函式**。
 *
 * 一筆運費對到多張工單時(出貨端寫入 `workOrderState = 'PENDING_ALLOCATION'`),
 * 歸單後要在進貨單的明細列上就地展開面板決定「這 1,450 元怎麼分給三張工單」。
 *
 * 抽成純函式而不是寫在表單元件裡,理由與 `accounting.ts` 那幾支相同:
 * 畫面要即時顯示百分比與金額、後端要把結果寫進
 * `purchase_order_line_work_orders.allocatedAmount`,兩邊算出不同的數字就是
 * 「同一筆成本有兩個答案」。
 *
 * ⚠️ 全部以**分(整數)**運算再轉回字串 —— 0.1 + 0.2 那類浮點誤差在分攤這種
 * 「加總必須剛好等於原值」的場景會直接變成對不起來的帳。
 */

/** 分攤方式。`BY_AMOUNT` 是預設(附圖上選中的那顆) */
export const FREIGHT_ALLOCATION_METHODS = {
  BY_AMOUNT: '依品項金額比例',
  EVEN: '平均分攤',
  MANUAL: '手動指定',
} as const;
export type FreightAllocationMethod = keyof typeof FREIGHT_ALLOCATION_METHODS;

export interface FreightAllocationTarget {
  /** 工單 / 案號(純單號,對得回 sales_orders.orderNo) */
  workOrderNo: string;
  /**
   * 這張工單的品項金額(`BY_AMOUNT` 的權重)。查不到就留白 —— 見下面
   * 「權重全為 0 時退回平均分攤」的說明。
   */
  baseAmount?: string | number | null;
  /** 使用者手動指定的金額(只有 `MANUAL` 會讀) */
  manualAmount?: string | number | null;
}

export interface FreightAllocationRow {
  workOrderNo: string;
  /** 分到的金額,固定 2 位小數 */
  amount: string;
  /** 佔比 0–100,1 位小數(純顯示用,不參與任何加總) */
  percent: number;
}

export interface FreightAllocationResult {
  rows: FreightAllocationRow[];
  /** `rows` 的金額合計。正常情況下逐分等於傳進來的 `totalAmount` */
  total: string;
  /**
   * 不為 `null` 時代表**這組分攤不可送出**,訊息直接顯示給使用者。
   * 目前只有兩種:沒有工單可分、以及手動指定的合計對不上本列金額。
   */
  error: string | null;
}

/** 字串 / 數字 → 分(四捨五入到整數分)。空值視為 0 */
function toCents(v: string | number | null | undefined): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** 分 → 固定 2 位小數的字串 */
function fromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * 把 `totalAmount` 分給 `targets`。
 *
 * **尾差一律補到第一列**(需求原文指定):先算出第 2..n 列的四捨五入結果,
 * 第一列拿「總額 − 其餘各列」。這樣加總永遠剛好等於總額,而且「差在哪一列」
 * 是固定的、看得懂的 —— 平均攤到各列會讓每一列都和百分比對不上。
 *
 * 三個刻意的邊界行為:
 *  1. **沒有工單** → `error`,而不是靜默回空陣列(那會讓使用者以為存好了)。
 *  2. **`BY_AMOUNT` 但權重全為 0**(工單金額查不到 / 真的是 0)→ **退回平均分攤**。
 *     除以 0 得不到比例,而擋下來也沒有意義:使用者要的是把這筆運費分掉。
 *  3. **總額為 0 或未填**(到付留白)→ 每一列都是 `0.00`,不是錯誤。
 */
export function allocateFreight(
  totalAmount: string | number | null | undefined,
  method: FreightAllocationMethod,
  targets: FreightAllocationTarget[],
): FreightAllocationResult {
  const totalCents = toCents(totalAmount);

  if (targets.length === 0) {
    return { rows: [], total: '0.00', error: '沒有可分攤的工單,請先指定歸屬工單' };
  }

  const percentOf = (cents: number): number =>
    totalCents === 0 ? 0 : Math.round((cents / totalCents) * 1000) / 10;

  // ── 手動指定:金額由使用者給,程式只負責把「加起來對不對」講清楚 ──
  if (method === 'MANUAL') {
    const cents = targets.map((t) => toCents(t.manualAmount));
    const sum = cents.reduce((a, b) => a + b, 0);
    const rows = targets.map((t, i) => ({
      workOrderNo: t.workOrderNo,
      amount: fromCents(cents[i] ?? 0),
      percent: percentOf(cents[i] ?? 0),
    }));
    return {
      rows,
      total: fromCents(sum),
      error:
        sum === totalCents
          ? null
          : `手動指定的金額合計 ${fromCents(sum)} 與本列金額 ${fromCents(totalCents)} 不符`,
    };
  }

  // ── 權重:依品項金額比例,或平均(權重全 1)──
  const weights =
    method === 'BY_AMOUNT' ? targets.map((t) => Math.max(0, toCents(t.baseAmount))) : [];
  const weightSum = weights.reduce((a, b) => a + b, 0);
  // BY_AMOUNT 但權重全為 0 → 退回平均分攤(見函式註解的邊界 2)
  const effective =
    method === 'BY_AMOUNT' && weightSum > 0 ? weights : targets.map(() => 1);
  const effectiveSum = effective.reduce((a, b) => a + b, 0);

  // 第 2..n 列各自四捨五入,第一列吃尾差
  const cents = effective.map((w, i) =>
    i === 0 ? 0 : Math.round((totalCents * w) / effectiveSum),
  );
  cents[0] = totalCents - cents.reduce((a, b) => a + b, 0);

  return {
    rows: targets.map((t, i) => ({
      workOrderNo: t.workOrderNo,
      amount: fromCents(cents[i] ?? 0),
      percent: percentOf(cents[i] ?? 0),
    })),
    total: fromCents(totalCents),
    error: null,
  };
}
